import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AlertCircle, TrendingDown, ShieldCheck, FileSearch } from 'lucide-react';
import PriceTable from './PriceTable';

const PricingSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const declarations = [
    {
      id: '01',
      title: '地域一番、低価格への挑戦',
      subtitle: '他社より1円でも高ければお値引き',
      description: '万が一、他社の見積額（同条件での比較）が当社の提示額より高かった場合、喜んでお値引きいたします。地域で一番低価格で安心して任せられる石材店を目指します。',
      color: 'from-emerald-500 to-teal-600',
      icon: TrendingDown,
      shadow: 'shadow-emerald-100',
      border: 'border-emerald-100'
    },
    {
      id: '02',
      title: '安心の「完全返金保証」特典',
      subtitle: '契約後1週間以内ならキャンセル無料',
      description: 'ご契約後、「気が変わった」「家族から反対があった」等の急なキャンセルでもご安心ください。契約後一週間以内であれば、キャンセル料は一切不要で、喜んでキャンセルを承ります。',
      color: 'from-blue-500 to-indigo-600',
      icon: ShieldCheck,
      shadow: 'shadow-blue-100',
      border: 'border-blue-100'
    },
    {
      id: '03',
      title: '明確な費用提示と丁寧な説明',
      subtitle: '追加費用の請求は一切ありません',
      description: 'お客様の墓地の状況をしっかりと確認のうえ、工事内容と費用内訳を明確にご提示します。ご納得いただけるまで丁寧にご説明いたします。',
      color: 'from-amber-500 to-orange-600',
      icon: FileSearch,
      shadow: 'shadow-amber-100',
      border: 'border-amber-100'
    }
  ];

  return (
    <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-stone-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#444_1px,transparent_1px)] [background-size:20px_20px]" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Heading */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-800 mb-4">
            安心の価格保証
          </h2>
          <div className="mt-6 mb-8 flex justify-center">
            <img src="images/92.png" alt="安心価格保証" className="max-w-full h-auto shadow-lg rounded-xl" />
          </div>          
        </motion.div>

        <div className="space-y-16">
          {/* Philosophy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-stone-100 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-2 h-full bg-amber-500" />
            <div className="space-y-6 text-stone-700 leading-relaxed text-lg">
              <p>
                集客の窓口に散見する比較サイトや相場見積もりサイトでは登録店への中間マージンや高額な登録料が品質低下や割高感が否めない現状があります。
              </p>
              <p>
                実際の費用は、お客様一人ひとりの墓地の設置状況、墓石の大きさ、基礎コンクリートの構造などにより大きく変動します。
                当社は、この「不透明さ」をなくし、お客様が心から安心して任せられる石材店を目指します。
              </p>
              <p>
                各地域や全国の平均費用を常に調査し、適正価格を追求。ワンストップサービスで、遠回りせずに「正しい手順」と「適切な費用感」をご提供します。
              </p>
              <p>
                一括見積サイトや比較サイトは一見便利ですが、SEO対策サイトへ誘導され、結果として高額な加盟料や契約手数料が施工品質に悪影響を及ぼす事例も残念ながら見受けられます。
              </p>
            </div>
          </motion.div>

          {/* Price Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            <PriceTable />
          </motion.div>

          {/* Variable cost points */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-stone-100 rounded-2xl p-8 md:p-10 border border-stone-200"
          >
            <div className="flex items-start gap-4 mb-8">
              <div className="bg-amber-100 p-3 rounded-full">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-stone-800 mb-2">費用が変動しやすい主なポイント</h3>
                <p className="text-stone-600">お墓の状況によって費用は変わります。だからこそ、現地確認が重要です。</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 mb-8">
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-stone-100">
                <span className="w-8 h-8 rounded-full bg-stone-800 text-white flex items-center justify-center font-bold text-sm">1</span>
                <span className="font-medium text-stone-700">墓石の大きさ・形・石の量</span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-stone-100">
                <span className="w-8 h-8 rounded-full bg-stone-800 text-white flex items-center justify-center font-bold text-sm">2</span>
                <span className="font-medium text-stone-700">区画の広さ、立地（山の中腹・斜面など）</span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-stone-100">
                <span className="w-8 h-8 rounded-full bg-stone-800 text-white flex items-center justify-center font-bold text-sm">3</span>
                <span className="font-medium text-stone-700">市営霊園・寺院など、霊園側の規定</span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-stone-100">
                <span className="w-8 h-8 rounded-full bg-stone-800 text-white flex items-center justify-center font-bold text-sm">4</span>
                <span className="font-medium text-stone-700">改葬許可申請や新しい納骨先の費用</span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-stone-100">
                <span className="w-8 h-8 rounded-full bg-stone-800 text-white flex items-center justify-center font-bold text-sm">5</span>
                <span className="font-medium text-stone-700">車両やクレーンの使用可否</span>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6 border border-amber-100 text-center">
              <p className="text-stone-800 font-medium">
                そのため、ホームページ上で「解体相場」を一律にお出しすることが難しいのが正直なところです。<br className="hidden sm:block" />
                まずは状況確認のうえ、分かりやすくご説明いたします。
              </p>
            </div>
          </motion.div>

          {/* Three Declarations (Redesigned) */}
          <div className="relative">
            <div className="text-center mb-12">
              <span className="text-amber-600 font-bold tracking-wider uppercase text-sm">Promise</span>
              <h3 className="text-3xl font-bold text-stone-800 mt-2">業界のオンリーワンをお約束する<br className="sm:hidden" />３つの安心宣言</h3>
              <div className="mt-8 flex justify-center">
                <img src="images/93.png" alt="業界オンリーワンの安心宣言" className="max-w-full h-auto shadow-lg rounded-xl" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {declarations.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className={`relative bg-white rounded-2xl p-8 shadow-xl border ${item.border} group hover:-translate-y-2 transition-transform duration-300`}
                  >
                    {/* Header Gradient */}
                    <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${item.color} rounded-t-2xl`} />
                    
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-8 h-8" />
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-4xl font-black text-stone-200 opacity-50 font-sans">{item.id}</span>
                        <h4 className="text-xl font-bold text-stone-800 leading-tight">{item.title}</h4>
                      </div>
                      <p className={`text-sm font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r ${item.color}`}>
                        {item.subtitle}
                      </p>
                      <p className="text-stone-600 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Closing CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-stone-900 rounded-2xl p-10 md:p-16 text-center shadow-2xl relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-stone-800 to-stone-900 z-0" />
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 z-0" />
            
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                まずは、費用を知ることから始めましょう
              </h3>
              <p className="text-stone-300 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                お客様のお墓の状況をお伺いし、正確なお見積もりを無料で作成いたします。<br />
                強引な勧誘は一切ございません。どうぞお気軽にご相談ください。
              </p>
              <button
                onClick={scrollToContact}
                className="px-12 py-5 bg-amber-600 hover:bg-amber-500 text-white text-lg font-bold rounded-full transition-all duration-300 shadow-lg hover:shadow-amber-500/30 hover:scale-105"
              >
                無料見積もり・お問い合わせ
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
